import React, { useEffect, useState } from 'react';
import { 
  Search,
  Mail,
  Users,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CreditCard
} from 'lucide-react';
import { balanceApi } from '../../../api/balance';
import { BalanceHistoryItem, BalanceHistoryResponse } from '../../../types/balance.types';
import Card from '../Card/Card';

const CreditHistory: React.FC = () => {
  const [transactions, setTransactions] = useState<BalanceHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchTransactionHistory();
  }, [currentPage]);

  const fetchTransactionHistory = async () => {
    try {
      if (transactions.length > 0) setIsPaginating(true);
      else setLoading(true);

      const response: BalanceHistoryResponse = await balanceApi.getHistory();
      const all = response.history || [];
      const computedTotalPages = Math.max(1, Math.ceil(all.length / itemsPerPage));
      const safePage = Math.min(currentPage, computedTotalPages);
      const pageStart = (safePage - 1) * itemsPerPage;
      const pageItems = all.slice(pageStart, pageStart + itemsPerPage);

      setTransactions(pageItems);
      setTotalPages(computedTotalPages);
      setHasNext(safePage < computedTotalPages);
      setHasPrevious(safePage > 1);
      setError(null);
    } catch (err) {
      setError('Failed to load transaction history');
      console.error('Error fetching transaction history:', err);
    } finally {
      setLoading(false);
      setIsPaginating(false);
    }
  };

  const handleNextPage = () => {
    if (hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const getTransactionIcon = (transaction: BalanceHistoryItem) => {
    switch (transaction.balance_type) {
      case 'searches':
        return <Search className="h-5 w-5 text-red-600" />;
      case 'dm_unlocks':
        return <Users className="h-5 w-5 text-red-600" />;
      case 'email_verifications':
        return <Mail className="h-5 w-5 text-red-600" />;
      default:
        return <CreditCard className="h-5 w-5 text-green-600" />;
    }
  };

  const getAmountColor = (amount: number) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getUnitLabel = (balanceType: BalanceHistoryItem['balance_type'], amountAbs: number) => {
    switch (balanceType) {
      case 'searches':
        return amountAbs === 1 ? 'search' : 'searches';
      case 'dm_unlocks':
        return amountAbs === 1 ? 'People enrichment' : 'People enrichments';
      case 'email_verifications':
        return amountAbs === 1 ? 'verification' : 'verifications';
      default:
        return 'units';
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="animate-pulse p-4 sm:p-6">
          <div className="mb-4 h-6 w-1/4 rounded bg-gray-200"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded bg-gray-100"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="p-4 sm:p-6">
          <div className="py-4 text-center text-red-600">{error}</div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-3 sm:p-6">
        <div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Usage History</h3>
        </div>

        {transactions.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            <Calendar className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p>No transactions yet</p>
          </div>
        ) : (
          <>
            <div className={`space-y-3 transition-opacity duration-200 ${isPaginating ? 'opacity-50' : 'opacity-100'}`}>
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 sm:p-4"
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5 shrink-0">
                      {getTransactionIcon(transaction)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium break-words text-gray-900">
                        {transaction.description || transaction.transaction_type}
                      </p>
                      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <span className={`text-sm font-semibold ${getAmountColor(transaction.amount)}`}>
                          {transaction.amount > 0 ? '+' : transaction.amount < 0 ? '-' : ''}
                          {Math.abs(transaction.amount).toLocaleString()}{' '}
                          {getUnitLabel(transaction.balance_type, Math.abs(transaction.amount))}
                        </span>
                        <span className="text-xs text-gray-500">
                          {transaction.created_at ? formatDate(transaction.created_at) : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex flex-col items-center justify-between gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:gap-4">
                <div className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handlePreviousPage}
                    disabled={!hasPrevious || isPaginating}
                    className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg font-medium transition-colors ${
                      hasPrevious && !isPaginating
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'cursor-not-allowed bg-gray-50 text-gray-400'
                    }`}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextPage}
                    disabled={!hasNext || isPaginating}
                    className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg font-medium transition-colors ${
                      hasNext && !isPaginating
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'cursor-not-allowed bg-gray-50 text-gray-400'
                    }`}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default CreditHistory;

